import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import trainerRowStyles from './TrainerRow.module.scss';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import Person4OutlinedIcon from '@mui/icons-material/Person4Outlined';
import EmptyState from '../composites/EmptyState';
import ContentDrill from '../composites/ContentDrill';
import TrainerRow from './TrainerRow';
import ListContent from '../composites/ListContent';
import AddTrainerDialog from './AddTrainerDialog';
import { useSessionData, useUserAction } from '../../providers';
import { fetchTrainersPageByGymId } from '../../api';
import { queryKeys } from '../../api/queryKeys';
import { usePaginatedListQuery } from '../../hooks/usePaginatedListQuery';

import type {
  AppState,
  TrainerDashboardData,
} from '../../appState/appInitialState';
import type { Trainer, Manager, WithOptionalId } from '../../types';

const trainerListTitles = ['Trainer Name', 'Clients', 'Options'];

export interface TrainersContentStateProps {
  manager?: Manager;
  allGymTrainers: TrainerDashboardData[];
  gymId: AppState['selectedGymId'];
}

export interface TrainersContentProps extends TrainersContentStateProps {}

const Trainers: React.FC = () => {
  const { createTrainer, updateTrainer } = useUserAction();
  const queryClient = useQueryClient();
  const { loginManager, gym, trainers: allGymTrainers } = useSessionData();
  const [selectedTrainer, setSelectedTrainer] = useState<string>('');
  const [isAddTrainerModalOpened, setIsAddTrainerModalOpened] = useState(false);

  const trainersListQuery = usePaginatedListQuery<Trainer>({
    enabled: Boolean(gym?.id),
    queryKey: (params) => queryKeys.trainers.gymPage(gym?.id || '', params),
    queryFn: (params) => fetchTrainersPageByGymId(gym!.id, params),
  });

  const filteredTrainers = useMemo(() => {
    return trainersListQuery.items
      .map((trainer) => {
        const hydratedTrainer = allGymTrainers[trainer.id]?.trainer;
        return {
          ...hydratedTrainer,
          ...trainer,
          gymIds:
            trainer.gymIds ?? hydratedTrainer?.gymIds ?? (gym ? [gym.id] : []),
          memberIds: trainer.memberIds ?? hydratedTrainer?.memberIds ?? [],
          trainingRoutineTemplateIds:
            trainer.trainingRoutineTemplateIds ??
            hydratedTrainer?.trainingRoutineTemplateIds ??
            [],
          exerciseIds:
            trainer.exerciseIds ?? hydratedTrainer?.exerciseIds ?? [],
          groupIds: trainer.groupIds ?? hydratedTrainer?.groupIds ?? [],
          memberCount:
            trainer.memberCount ??
            hydratedTrainer?.memberCount ??
            hydratedTrainer?.memberIds?.length ??
            0,
        };
      })
      .sort(
        (a, b) =>
          a.firstName
            ?.toLowerCase()
            .localeCompare(b.firstName?.toLowerCase() || '') || 0,
      );
  }, [allGymTrainers, gym, trainersListQuery.items]);

  const trainersPagination = {
    page: trainersListQuery.page,
    pageSize: trainersListQuery.pageSize,
    totalElements: trainersListQuery.meta.totalElements,
    totalPages: trainersListQuery.meta.totalPages,
    onPageChange: trainersListQuery.setPage,
    onPageSizeChange: trainersListQuery.setPageSize,
    disabled: trainersListQuery.isFetching,
  };

  const invalidateTrainersPage = () => {
    void queryClient.invalidateQueries({ queryKey: ['trainers'] });
  };

  if (!loginManager || !gym) {
    return (
      <EmptyState icon={<Person4OutlinedIcon />} text="No trainers found." />
    );
  }

  const renderEmptyState = () => (
    <EmptyState
      icon={<Person4OutlinedIcon />}
      cta={{
        icon: <Person4OutlinedIcon color="primary" />,
        label: 'Add A Trainer',
        onClick: () => setIsAddTrainerModalOpened(true),
      }}
      text="No Trainers Have Been Added"
    />
  );

  const addTrainer = async (
    trainer: WithOptionalId<Trainer>,
  ): Promise<{ success: boolean; emailAlreadyInUse?: boolean }> => {
    const result = await createTrainer(trainer, gym?.id || '');
    if (result.success) {
      setIsAddTrainerModalOpened(false);
      invalidateTrainersPage();
    }
    return result;
  };

  const renderAddProgramDialog = () => (
    <AddTrainerDialog
      onSubmit={addTrainer}
      onClose={() => setIsAddTrainerModalOpened(false)}
      isOpen={isAddTrainerModalOpened}
    />
  );

  const deleteTrainer = async (trainer: Trainer) => {
    await updateTrainer({ ...trainer, isActive: false });
    invalidateTrainersPage();
  };

  const renderContent = () => {
    return (
      <ListContent
        items={filteredTrainers}
        isLoading={trainersListQuery.isLoading}
        listHeaderClassName={trainerRowStyles.listGrid}
        pagination={trainersPagination}
        EmptyStateComponent={renderEmptyState}
        titles={trainerListTitles}
        ItemComponent={({ data }) => (
          <TrainerRow
            trainer={data}
            deleteTrainer={deleteTrainer}
            selected={selectedTrainer === data.id}
            onSelect={() => setSelectedTrainer(data.id)}
            key={data.id}
          />
        )}
      />
    );
  };

  const getSearchPlaceholder = () => {
    if (!filteredTrainers.length) return '';
    return 'Search for Trainers';
  };

  return (
    <ContentDrill
      title="Trainers"
      isParent
      actions={[
        {
          icon: <AddBoxOutlinedIcon />,
          label: 'Add A Trainer',
          onClick: () => setIsAddTrainerModalOpened(true),
        },
      ]}
      searchPlaceholder={getSearchPlaceholder()}
      searchQuery={trainersListQuery.searchQuery}
      setSearchQuery={trainersListQuery.setSearchQuery}
      disableSearch={false}
    >
      {renderContent()}
      {renderAddProgramDialog()}
    </ContentDrill>
  );
};

export default Trainers;
